import {
  FullAutoCouponsTargetingCheckStatus,
  IFullAutoCouponsTargetingMetrics,
  SDK_TEST_FRESH_DAYS,
} from "./full-auto-coupons-targeting";
import { CouponIssueType, TCouponIssueCounts } from "./coupon-issues";
import type { IWorkPipelineTemplateHelp } from "./template-pipeline";

/**
 * Work Pipeline
 * -------------
 * A self-maintaining board of QA work. Each brand (QA-list item) sits in
 * exactly ONE stage, derived from data we already capture (the full-auto
 * coupons snapshot, SDK tests, template syncs). See docs/work-pipeline-plan.md.
 *
 * Two pre-snapshot buckets funnel work before the snapshot-driven stages:
 *   CodeHunting      - no active coupon; tester hunts for codes (replaces parked NoCoupons).
 *   AwaitingSnapshot - has coupons but no (fresh) snapshot; needs one generated.
 *
 * Open coupon issues (see coupon-issues.ts) come before all of it, snapshot or not:
 *   CouponReview     (tester)  codes to suspend / re-enable
 *   TemplateReview   (tester)  shoppers got "Coupon box not found"
 *
 * The snapshot-driven stages, in order (earliest unmet wins):
 *   1 CodeTesting      (tester)  metadata / coverage
 *   2 Targeting        (tester)  coupon targeting
 * 2.5 SessionCapture   (tester)  site needs a logged-in session a bot cannot get
 *   3 TemplateBuilding (dev)     auto / sku template
 * 3.5 TemplateReview  (tester)  session-run failures on existing templates
 *   4 ApkTesting       (tester)  SDK end-to-end test
 *   5 DevFixes         (dev)     failed APK test -> code change
 *
 * Terminal / non-work states:
 *   Complete - everything green + SDK test fresh; leaves the board.
 *   Error    - snapshot run hit an infrastructure error; needs a re-run.
 */
export enum WorkPipelineStage {
  CodeHunting = "code-hunting",
  AwaitingSnapshot = "awaiting-snapshot",
  CodeTesting = "code-testing",
  CouponReview = "coupon-review",
  Targeting = "targeting",
  SessionCapture = "session-capture",
  TemplateBuilding = "template-building",
  TemplateReview = "template-review",
  ApkTesting = "apk-testing",
  DevFixes = "dev-fixes",
  Complete = "complete",
  Error = "error",
}

/** Which role owns the work in a given stage. */
export type WorkPipelineOwnerRole = "tester" | "developer" | "none";

/** State of the underlying full-auto-coupons snapshot for an item. */
export type WorkPipelineSnapshotState =
  | "present" // a usable snapshot exists
  | "missing" // no snapshot has ever been generated
  | "stale" // a snapshot exists but is older than the freshness window
  | "error"; // the snapshot run recorded an infrastructure error

export const WORK_PIPELINE_STAGE_LABELS: Record<WorkPipelineStage, string> = {
  [WorkPipelineStage.CodeHunting]: "Code hunting",
  [WorkPipelineStage.AwaitingSnapshot]: "Awaiting snapshot",
  [WorkPipelineStage.CodeTesting]: "Code testing",
  [WorkPipelineStage.CouponReview]: "Coupon review",
  [WorkPipelineStage.Targeting]: "Targeting",
  [WorkPipelineStage.SessionCapture]: "Session capture",
  [WorkPipelineStage.TemplateBuilding]: "Template building",
  [WorkPipelineStage.TemplateReview]: "Template review",
  [WorkPipelineStage.ApkTesting]: "APK testing",
  [WorkPipelineStage.DevFixes]: "Dev fixes",
  [WorkPipelineStage.Complete]: "Complete",
  [WorkPipelineStage.Error]: "Error",
};

export const WORK_PIPELINE_STAGE_OWNER: Record<
  WorkPipelineStage,
  WorkPipelineOwnerRole
> = {
  [WorkPipelineStage.CodeHunting]: "tester",
  [WorkPipelineStage.AwaitingSnapshot]: "none",
  [WorkPipelineStage.CodeTesting]: "tester",
  [WorkPipelineStage.CouponReview]: "tester",
  [WorkPipelineStage.Targeting]: "tester",
  [WorkPipelineStage.SessionCapture]: "tester",
  [WorkPipelineStage.TemplateBuilding]: "tester",
  [WorkPipelineStage.TemplateReview]: "tester",
  [WorkPipelineStage.ApkTesting]: "tester",
  [WorkPipelineStage.DevFixes]: "developer",
  [WorkPipelineStage.Complete]: "none",
  [WorkPipelineStage.Error]: "none",
};

/** Left-to-right ordering for tabs / columns on the board. */
export const WORK_PIPELINE_STAGE_ORDER: WorkPipelineStage[] = [
  WorkPipelineStage.CodeHunting,
  WorkPipelineStage.AwaitingSnapshot,
  WorkPipelineStage.CodeTesting,
  WorkPipelineStage.CouponReview,
  WorkPipelineStage.Targeting,
  WorkPipelineStage.SessionCapture,
  WorkPipelineStage.TemplateBuilding,
  WorkPipelineStage.TemplateReview,
  WorkPipelineStage.ApkTesting,
  WorkPipelineStage.DevFixes,
  WorkPipelineStage.Complete,
  WorkPipelineStage.Error,
];

/**
 * What a dev says about a Dev fixes row. Before a dev picks one the row shows
 * as "Escalated" (the tester's hand-off). Blocked and Redo are retired but kept
 * so older rows still read.
 */
export enum WorkPipelineDevStatus {
  InProgress = "in-progress",
  Blocked = "blocked",
  ReadyForTesting = "ready-for-testing",
  Redo = "redo",
  CouponIssue = "coupon-issue",
  NoDeal = "no-deal",
  Disabled = "disabled",
}

/** The statuses a dev can pick now. */
export const WORK_PIPELINE_DEV_STATUS_OPTIONS: WorkPipelineDevStatus[] = [
  WorkPipelineDevStatus.InProgress,
  WorkPipelineDevStatus.ReadyForTesting,
  WorkPipelineDevStatus.CouponIssue,
  WorkPipelineDevStatus.NoDeal,
  WorkPipelineDevStatus.Disabled,
];

/** Shown while a hand-off waits for a dev's first status. */
export const WORK_PIPELINE_DEV_ESCALATED_LABEL = "Escalated";

/** The block reason a dev's "Disabled" proposes; managers approve it into Archived. */
export const WORK_PIPELINE_DEV_DISABLED_REASON = {
  code: "disabled-by-dev",
  label: "Disabled by dev",
  description: "A dev disabled it because it can't be fixed.",
};

export const WORK_PIPELINE_DEV_STATUS_LABELS: Record<
  WorkPipelineDevStatus,
  string
> = {
  [WorkPipelineDevStatus.InProgress]: "In progress",
  [WorkPipelineDevStatus.Blocked]: "Blocked",
  [WorkPipelineDevStatus.ReadyForTesting]: "Ready for testing",
  [WorkPipelineDevStatus.Redo]: "Redo",
  [WorkPipelineDevStatus.CouponIssue]: "Coupon issue",
  [WorkPipelineDevStatus.NoDeal]: "Deals issue",
  [WorkPipelineDevStatus.Disabled]: "Disabled",
};

/** Buckets where the Claimed filter means the dev's claim, not the tester's. */
export const WORK_PIPELINE_DEV_CLAIM_STAGES: WorkPipelineStage[] = [
  WorkPipelineStage.DevFixes,
];

/** One step of a dev hand-off; kept across hand-backs and re-escalations so the context survives. */
export interface IWorkPipelineDevHistoryEntry {
  at: string;
  kind: "escalated" | "status";
  byId: string | null;
  byName: string | null;
  status: WorkPipelineDevStatus | null;
  /** Where it came from (escalated) or where the status sent it (status). */
  stage: WorkPipelineStage | null;
  note: string | null;
}

export type WorkPipelineTemplateKind = "auto" | "sku";

/**
 * A template is disabled when SQL has rows for the domain but none is active,
 * or when a manager approved a dev's "Disabled" block. Tracking only.
 */
export interface IWorkPipelineTemplatesDisabled {
  auto: boolean;
  sku: boolean;
  /** An approved "Disabled by dev" block, whichever templates it named. */
  byDev: boolean;
}

export const WORK_PIPELINE_TEMPLATE_DISABLED_LABELS: Record<
  WorkPipelineTemplateKind,
  string
> = {
  auto: "Auto Template Disabled",
  sku: "SKU Template Disabled",
};

export const WORK_PIPELINE_TEMPLATE_KIND_LABELS: Record<
  WorkPipelineTemplateKind,
  string
> = {
  auto: "Auto template",
  sku: "SKU template",
};

/** These send the row back to a tester; In progress keeps it with the dev, Disabled proposes a block. */
export const WORK_PIPELINE_DEV_HANDBACK: WorkPipelineDevStatus[] = [
  WorkPipelineDevStatus.ReadyForTesting,
  WorkPipelineDevStatus.Redo,
  WorkPipelineDevStatus.CouponIssue,
  WorkPipelineDevStatus.NoDeal,
];

/** Where a hand-back sends the row: a coupon issue goes to the code tester first, the rest to the retest. */
export const devHandbackStage = (
  status: WorkPipelineDevStatus,
  returnStage: WorkPipelineStage | null
): WorkPipelineStage =>
  status === WorkPipelineDevStatus.CouponIssue
    ? WorkPipelineStage.CouponReview
    : (returnStage ?? WorkPipelineStage.ApkTesting);

/** What the tester does next after a hand-back. */
export const devHandbackNext = (status: WorkPipelineDevStatus | null): string =>
  status === WorkPipelineDevStatus.Disabled
    ? "can't be fixed; close it."
    : status === WorkPipelineDevStatus.CouponIssue
      ? "fix the code, then it goes to APK testing for a retest."
      : status === WorkPipelineDevStatus.NoDeal
        ? "escalated to Deals; retest once they reply."
        : "retest and complete.";

/** The outcome a tester records when finishing a claimed brand. */
export enum WorkPipelineOutcome {
  Completed = "completed",
  Broken = "broken", // -> Dev fixes
  NeedsHelp = "needs-help", // -> escalate (reassign to a named lead)
  DealMismatch = "deal-mismatch", // -> Deals queue ("Escalate to Deals": merge or mismatch)
}

/** A lead's "work this first" flag on a merchant (domain + geo); both pipelines sort it to the top. */
export interface IWorkPipelineUrgent {
  /** Bare, lowercased; matches www. rows too. */
  domain: string;
  /** Kindred spelling (UK, not GB); matches either on the board. */
  countryCode: string;
  note: string | null;
  setBy: string | null;
  setByName: string | null;
  setAt: string;
}

export interface IWorkPipelineUrgentBody {
  items: { domain: string; countryCode: string }[];
  note?: string;
}

/**
 * One-run rule for template creation in the extension. Defaults until the QA
 * manager confirms them; the server gate and the extension both read these.
 * A run fails when a template type it set out to build gets a Fail verdict (or
 * a Pass whose save failed). Crashes, infra errors, aborts and runs left
 * without a verdict don't count.
 */
export const TEMPLATE_RUN_RULE = {
  /** Failed tester runs per merchant (domain + geo) before only a lead may run it. */
  testerFailuresAllowed: 1,
  /** What the extension shows when the gate refuses a run. */
  blockedMessage: "Failed once — a lead needs to run it.",
  /** A tester's failed run goes to the lead queue (Need assistance). */
  testerFailureToLeads: true,
  /** A lead's failed run is marked Broken and goes to Dev fixes. */
  leadFailureToDevFixes: true,
  /** The count starts again when the row re-enters Template building. */
  resetOnTemplateBuildingEntry: true,
  /** The count starts again when a template for the domain syncs to SQL. */
  resetOnTemplateSync: true,
};

/** "stopped": a tester ended a run that never reported back (e.g. the site reloaded); it doesn't count. */
export type TWorkPipelineTemplateRunOutcome = "running" | "passed" | "failed" | "stopped";

/** The block reason "Stop run" proposes when the site reloads mid-run, so nobody builds it again. */
export const WORK_PIPELINE_SITE_REFRESH_REASON = {
  code: "site-refreshes",
  label: "Site refreshes mid-run",
  description: "The site reloads during a template run, so the extension can't build a template for it.",
};

export interface IWorkPipelineStopTemplateRunBody {
  note?: string;
  /** Also propose a "Site refreshes mid-run" block. */
  siteRefresh?: boolean;
}

/** The template types a run sets out to build; it passes only when each of them passes. */
export type TTemplateRunBuild = "auto" | "sku";

/** What a run builds, from the panel's run mode. */
export const templateRunBuilds = (
  runMode: "both" | "auto-only" | "sku-only" | null | undefined
): TTemplateRunBuild[] =>
  runMode === "auto-only" ? ["auto"] : runMode === "sku-only" ? ["sku"] : ["auto", "sku"];

/** A template-creation run from the extension, tied to the row it was run for. */
export interface IWorkPipelineTemplateRun {
  correlationId: string;
  byId: string | null;
  byName: string | null;
  byLead: boolean;
  /** The types it set out to build. */
  builds: TTemplateRunBuild[];
  startedAt: string;
  outcome: TWorkPipelineTemplateRunOutcome;
  finishedAt: string | null;
  /** The reviewer's failure reason, when it failed. */
  note: string | null;
  /** When a template for the domain synced to SQL after this run passed. */
  syncedAt: string | null;
}

export interface IWorkPipelineItem {
  _id: string;
  qaListId: string;
  qaListName: string;
  qaItemId: string;
  domain: string;
  countryCode: string;
  brandId: string | null;
  brandName: string | null;

  stage: WorkPipelineStage;
  /** Human-readable "why is it here" e.g. "targeting 3/12" or "SDK 18 days old". */
  stageReason: string;
  /** When the item first entered its current stage (drives waiting-time). */
  waitingSince: string;
  /** Priority within a stage for board ordering (lower = more urgent).
   *  In CodeTesting this reflects coupon expiry proximity. */
  stagePriority: number;
  /** Which component failed, set when a tester records Broken on an APK test.
   *  Tells developers which codebase to investigate. */
  devFailType: "checkout-extension" | "apk" | null;

  /** Cached from the item's full-auto-coupons snapshot so the board list can
   *  show APK test status/last-tested without an extra lookup. Null when no
   *  snapshot/test exists yet. */
  lastSdkTestStatus: FullAutoCouponsTargetingCheckStatus | null;
  lastSdkTestResult: "pass" | "fail" | null;
  lastSdkTestAt: string | null;

  snapshotState: WorkPipelineSnapshotState;
  snapshotDate: string | null;

  /**
   * False when the row has left the active board (its list was un-flagged or
   * the item was removed) — kept for history rather than deleted. The board
   * shows only active rows.
   */
  isActive: boolean;

  /**
   * The full-auto-coupons standalone tracking-item id this brand maps to (the
   * snapshot's `qaItemId`). Used to deep-link into the targeting/testing page.
   * Null until a snapshot exists.
   */
  facItemId: string | null;

  /**
   * True when a manager manually moved this brand to a stage. While set, the
   * recompute/sweep leaves the stage alone (it still refreshes snapshot state).
   */
  manualOverride: boolean;

  // Claim / ownership (reuses the processing-lock idea for humans).
  assignedTo: string | null;
  assignedToName: string | null;
  claimedAt: string | null;
  /** Why the last holder let it go, for whoever picks it up next. Cleared on claim. */
  releaseNote: string | null;
  releaseNoteByName: string | null;
  releaseNoteAt: string | null;
  /**
   * Broken hands the row to a dev; the tester keeps the claim, and the row returns
   * to `devReturnStage` for them to retest once the dev sends feedback.
   */
  devEscalatedAt: string | null;
  devEscalatedByName: string | null;
  devReturnStage: WorkPipelineStage | null;
  devClaimedBy: string | null;
  devClaimedByName: string | null;
  /** The dev on the latest fix; survives handback so they can follow the retest. */
  devOwner: string | null;
  devOwnerName: string | null;
  /** The dev's latest status and comment, as on the old dev sheet. */
  devStatus: WorkPipelineDevStatus | null;
  devStatusByName: string | null;
  devStatusAt: string | null;
  devFeedback: string | null;
  devFeedbackByName: string | null;
  /** Set when the dev handed it back to the tester to retest. */
  devFeedbackAt: string | null;
  /** Every escalation and dev status, oldest first. */
  devHistory: IWorkPipelineDevHistoryEntry[];
  /** Which templates the dev's "Disabled" named; they count once a manager approves the block. */
  devDisabledTemplates: WorkPipelineTemplateKind[];
  /** Set when a manager approves the dev's "Disabled"; the row stays on the board, tagged. */
  devDisableApprovedAt: string | null;
  devDisableApprovedByName: string | null;
  templatesDisabled: IWorkPipelineTemplatesDisabled;
  urgent: IWorkPipelineUrgent | null;
  /** Set while the agent's walk for this domain + geo has an open request for help. */
  templateHelp: IWorkPipelineTemplateHelp | null;
  /** Extension template-creation runs for this row, oldest first. */
  templateRuns: IWorkPipelineTemplateRun[];
  /** Runs before this don't count towards TEMPLATE_RUN_RULE. */
  templateRunsSince: string | null;

  /** The open Coupon Performance review step on this row; null when there is none. */
  couponReviewStep: "code" | "apk" | "dev" | null;

  // Escalation: "needs further help" enters the shared Lead QA Testers queue.
  escalatedTo: string | null;
  escalatedToName: string | null;

  /** Assistance stays attached to the tester-owned domain while a lead
   * reviews the issue and responds. */
  assistance: IWorkPipelineAssistance;

  /** Open Deals escalation (merge or mismatch); the tester keeps the claim. */
  dealMismatchNote: string | null;
  dealMismatchByName: string | null;
  dealMismatchAt: string | null;

  /** Deals' reply to the last escalation; closes it and unlocks release. */
  dealFeedback: string | null;
  dealFeedbackByName: string | null;
  dealFeedbackAt: string | null;

  /** A lead working a Help/Deals escalation, alongside the tester's claim. */
  leadClaimedBy: string | null;
  leadClaimedByName: string | null;
  leadClaimedAt: string | null;

  /** Tester-recorded reason the brand isn't workable (null = workable). */
  brandStatus: WorkPipelineBrandStatus | null;

  /**
   * When a tester last actually worked this brand. Distinct from waitingSince,
   * which resets whenever the stage changes and so under-reports recent work.
   */
  lastCheckedAt: string | null;

  /** Block/archive workflow state. */
  block: IWorkPipelineBlock;
  /** True once a manager approved the block — hidden from the board. */
  archived: boolean;

  /** Tester's record of a fruitless code hunt. */
  huntNote: string | null;
  huntSourcesChecked: string[];

  lastComputedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkPipelineAssistance {
  requestedBy: string | null;
  requestedByName: string | null;
  requestedAt: string | null;
  issue: string | null;
  leadId: string | null;
  leadName: string | null;
  feedback: string | null;
  respondedBy: string | null;
  respondedByName: string | null;
  respondedAt: string | null;
}

export interface IWorkPipelineAssistanceResponse {
  feedback: string;
}

/** Why a brand can't be worked, recorded by the tester on the board.
 *  Seeds the managed block-reason catalogue; new reasons are added as data. */
export enum WorkPipelineBrandStatus {
  NoCouponCodeField = "no-coupon-code-field",
  NotEcomm = "not-ecomm",
  AccessDenied = "access-denied",
}

/** A manager-editable reason a brand can be blocked/archived. */
export interface IWorkPipelineBlockReason {
  _id: string;
  code: string;
  label: string;
  description: string;
  /** Inactive reasons stay readable on historic rows but can't be picked. */
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkPipelineBlockReasonInput {
  code?: string;
  label: string;
  description?: string;
  isActive?: boolean;
}

/** A block runs tester-proposed -> manager-decided. */
export enum WorkPipelineBlockState {
  Proposed = "proposed",
  Approved = "approved",
  Rejected = "rejected",
}

export interface IWorkPipelineBlock {
  reasonCode: string | null;
  reasonLabel: string | null;
  state: WorkPipelineBlockState | null;
  note: string | null;
  proposedBy: string | null;
  proposedByName: string | null;
  proposedAt: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export interface IWorkPipelineBlockProposal {
  reasonCode: string;
  note?: string;
}

export interface IWorkPipelineBlockDecision {
  approve: boolean;
  reviewNote?: string;
}

export const WORK_PIPELINE_BRAND_STATUS_LABELS: Record<
  WorkPipelineBrandStatus,
  string
> = {
  [WorkPipelineBrandStatus.NoCouponCodeField]: "No coupon code field",
  [WorkPipelineBrandStatus.NotEcomm]: "Not ecomm",
  [WorkPipelineBrandStatus.AccessDenied]: "Access denied",
};

/** A QA list represented on the board, for the list filter. Counts are
 *  active, non-archived rows so a manager can see a list drain stage by
 *  stage. */
export interface IWorkPipelineListOption {
  qaListId: string;
  qaListName: string;
  total: number;
  complete: number;
}

export interface IWorkPipelineStageCount {
  stage: WorkPipelineStage;
  total: number;
  claimed: number;
  urgent: number;
  /** Oldest waitingSince in the stage (ISO) — the top of the bottleneck. */
  oldestWaitingSince: string | null;
}

// ── Status report ────────────────────────────────────────────
// A read-only "what is happening right now" view. Every count is deduped the
// same way the board is, so the report and the stage tabs never disagree.

export interface IWorkPipelineReportStage {
  stage: WorkPipelineStage;
  total: number;
  claimed: number;
  unclaimed: number;
  /** Items that have sat in this stage longer than the ageing threshold. */
  aged: number;
  oldestWaitingSince: string | null;
}

export interface IWorkPipelineReportList {
  qaListId: string;
  qaListName: string;
  total: number;
  complete: number;
  /** Count per stage; stages with nothing in them are omitted. */
  byStage: Partial<Record<WorkPipelineStage, number>>;
}

export interface IWorkPipelineReportCountry {
  countryCode: string;
  total: number;
  complete: number;
}

/** Work that has fallen out of the normal flow and needs someone to act. */
export interface IWorkPipelineReportQueues {
  escalated: number;
  dealMismatch: number;
  blocksAwaitingReview: number;
  archived: number;
  errored: number;
}

/** Counted from audit events, so "entered complete" rather than "completed" —
 *  a brand that regresses and is fixed again is counted on both days. */
export interface IWorkPipelineReportDay {
  day: string; // YYYY-MM-DD
  enteredComplete: number;
  hunts: number;
  outcomesRecorded: number;
}

export interface IWorkPipelineReport {
  generatedAt: string;
  /** Days a brand may sit in one stage before it counts as aged. */
  agedAfterDays: number;
  activeTotal: number;
  completeTotal: number;
  claimedTotal: number;
  unclaimedTotal: number;
  stages: IWorkPipelineReportStage[];
  lists: IWorkPipelineReportList[];
  countries: IWorkPipelineReportCountry[];
  queues: IWorkPipelineReportQueues;
  throughput: IWorkPipelineReportDay[];
}

/** Template outcomes per QA list over a date range. One domain + geo counts once per list. */
export interface IWorkPipelineListTemplateReportRow {
  qaListId: string;
  qaListName: string;
  /** Set on lists the system fills itself, e.g. "template-help" (Agent needs help). */
  systemKey: string | null;
  /** Domains on the list, plus ones that left it during the range. */
  total: number;
  /** Worked by a person (any board action) during the range. */
  handled: number;
  /** Live now: an active auto template in SQL for the domain. */
  live: number;
  /** A template for the domain synced to SQL during the range. */
  wentLive: number;
  /** Blocked or given a can't-test brand status during the range. */
  blocked: number;
  blockedByReason: { reason: string; count: number }[];
  /** Not live, not blocked and not complete. */
  open: number;
}

export interface IWorkPipelineTesterTemplateRow {
  actor: string;
  actorName: string;
  handled: number;
  /** Of the domains they handled, live now. */
  live: number;
  /** Blocks they proposed or can't-test brand statuses they set. */
  blocked: number;
}

export interface IWorkPipelineListTemplateReport {
  generatedAt: string;
  dateFrom: string;
  dateTo: string;
  lists: IWorkPipelineListTemplateReportRow[];
  testers: IWorkPipelineTesterTemplateRow[];
  /** False when SQL couldn't be read, so live counts are missing rather than zero. */
  liveKnown: boolean;
}

export interface IWorkPipelineReportFilter {
  qaListId?: string;
  excludeAgentic?: boolean;
  /** How many days of throughput to return. */
  days?: number;
}

/** Board ordering. Waiting time is stage-scoped; last-checked is brand-scoped. */
export enum WorkPipelineSort {
  WaitingLongest = "waiting-longest",
  LeastRecentlyChecked = "least-recently-checked",
}

export const WORK_PIPELINE_SORT_LABELS: Record<WorkPipelineSort, string> = {
  [WorkPipelineSort.WaitingLongest]: "Waiting longest",
  [WorkPipelineSort.LeastRecentlyChecked]: "Least recently checked",
};

export interface IWorkPipelineFilter {
  qaListId?: string;
  stage?: WorkPipelineStage;
  assignee?: string;
  /** This user's claims as tester or dev, plus open fixes they handed back. */
  mine?: string;
  /** "claimed" = has an owner, "unclaimed" = free to pick up. The dev's claim in WORK_PIPELINE_DEV_CLAIM_STAGES, the tester's elsewhere. */
  claimed?: "claimed" | "unclaimed";
  /** "pinned" = manually moved and frozen against recompute, "auto" = the
   *  stage is still derived from data. */
  pinned?: "pinned" | "auto";
  /** Only items a tester explicitly escalated to a support lead. */
  escalatedOnly?: boolean;
  /** Only items with an unresolved tester-reported deal mismatch. */
  dealMismatchOnly?: boolean;
  countryCode?: string;
  search?: string;
  /** Hide brands the agentic runner already covers, so manual testers skip them. */
  excludeAgentic?: boolean;
  /** Only brands untouched for at least this many days (never-checked included). */
  notCheckedDays?: number;
  sort?: WorkPipelineSort;
  /** Board hides archived rows by default; these surface them for review. */
  archived?: boolean;
  blockState?: WorkPipelineBlockState;
  blockReasonCode?: string;
  /** Only domains with a disabled auto or SKU template. */
  templateDisabled?: boolean;
  /** Only domains a lead flagged urgent. */
  urgentOnly?: boolean;
  /** Only rows the agent is stuck on: a help kind (HelpKind) or "any". */
  templateHelpKind?: string;
  page?: number;
  limit?: number;
}

// ── Audit log ────────────────────────────────────────────────

export enum WorkPipelineAuditEventType {
  StageChanged = "stage-changed", // auto-derived stage transition
  Claimed = "claimed", // tester claimed a brand
  Released = "released", // tester/manager released a brand
  OutcomeRecorded = "outcome-recorded", // completed / broken / needs-help
  ManualMove = "manual-move", // manager pinned to a specific stage
  AutoReset = "auto-reset", // manager removed manual pin (re-derive)
  SnapshotAdded = "snapshot-added", // brand added to snapshot queue
  SessionRunFailed = "session-run-failed", // agentic runner found a template failure
  Reopened = "reopened", // a completed brand was put back into work
  EntityEdited = "entity-edited", // tester saved a coupon/deal/offer edit
  BrandStatusSet = "brand-status-set", // tester flagged the brand as unworkable
  BlockProposed = "block-proposed", // tester asked for the brand to be archived
  BlockApproved = "block-approved", // manager archived it
  BlockRejected = "block-rejected", // manager sent it back to work
  BlockRestored = "block-restored", // manager un-archived it
  HuntRecorded = "hunt-recorded", // tester logged a code hunt with no result
  UrgentSet = "urgent-set", // lead flagged the domain urgent
  UrgentCleared = "urgent-cleared", // lead took the flag off
}

/** Links a pipeline audit row back to the SQL entity it touched. */
export interface IWorkPipelineAuditEntityRef {
  entityType: "deal" | "coupon" | "offer" | "brand";
  entityId: string;
  entityName?: string | null;
}

export interface IWorkPipelineAuditEvent {
  _id: string;
  domain: string;
  countryCode: string;
  /** userId or "system" for automated events. */
  actor: string;
  actorName: string;
  eventType: WorkPipelineAuditEventType;
  fromStage: WorkPipelineStage | null;
  toStage: WorkPipelineStage | null;
  detail: string;
  /** SQL entities touched, so the activity feed can pull their column diffs. */
  entityRefs: IWorkPipelineAuditEntityRef[];
  createdAt: string;
}

export interface IWorkPipelineAuditFilter {
  domain?: string;
  countryCode?: string;
  actor?: string;
  eventType?: WorkPipelineAuditEventType;
  /** ISO date string for the start of the range. Defaults to start of today. */
  dateFrom?: string;
  /** ISO date string for the end of the range. Defaults to end of today. */
  dateTo?: string;
  page?: number;
  limit?: number;
}

/** One logged code hunt. Hunts are audit events, so the history survives the
 *  brand moving on out of the hunting bucket. */
export interface IWorkPipelineHuntRecord {
  _id: string;
  domain: string;
  countryCode: string;
  actor: string;
  actorName: string;
  /** What the tester wrote plus the sources they checked, as logged. */
  detail: string;
  createdAt: string;
}

export interface IWorkPipelineHuntFilter {
  domain?: string;
  countryCode?: string;
  actor?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

// ── Completion readiness ("incomplete warning" gating) ───────

/** Blockers stop "mark complete"; warnings only need acknowledging. */
export type WorkPipelineReadinessSeverity = "blocker" | "warning";

export enum WorkPipelineReadinessCode {
  NoActiveCoupon = "no-active-coupon",
  CouponMissingMetadata = "coupon-missing-metadata",
  CouponMissingSummary = "coupon-missing-summary",
  CouponUntested = "coupon-untested",
  CouponMissingTargeting = "coupon-missing-targeting",
  CoverageMismatch = "coverage-mismatch",
  DuplicateSiteWidePriority = "duplicate-site-wide-priority",
  ExpiredCoupon = "expired-coupon",
  OpenCouponIssue = "open-coupon-issue",
  TemplateNotLive = "template-not-live",
  NoPassingTemplateRun = "no-passing-template-run",
}

export interface IWorkPipelineReadinessIssue {
  code: WorkPipelineReadinessCode;
  severity: WorkPipelineReadinessSeverity;
  message: string;
  entityType: "coupon" | "domain" | null;
  entityId: string | null;
  entityLabel: string | null;
}

export interface IWorkPipelineReadiness {
  /** False when at least one blocker is present — "mark complete" is disabled. */
  ready: boolean;
  issues: IWorkPipelineReadinessIssue[];
}

// ── Per-actor activity & productivity ────────────────────────

/** Pipeline events come from Mongo; entity events come from the SQL audit. */
export type WorkPipelineActivitySource = "pipeline" | "entity";

export interface IWorkPipelineActivityFieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface IWorkPipelineActivityEntry {
  source: WorkPipelineActivitySource;
  at: string;
  actor: string;
  actorName: string;
  domain: string | null;
  countryCode: string | null;
  entityType: "deal" | "coupon" | "offer" | "brand" | null;
  entityId: string | null;
  entityLabel: string | null;
  /** Audit event type for pipeline rows, or the SQL comment for entity rows. */
  action: string;
  detail: string;
  fieldChanges: IWorkPipelineActivityFieldChange[];
}

export interface IWorkPipelineActivityFilter {
  actor?: string;
  domain?: string;
  /** ISO date string. Defaults to start of today. */
  dateFrom?: string;
  /** ISO date string. Defaults to end of today. */
  dateTo?: string;
  page?: number;
  limit?: number;
}

/** One row per (actor, day) for the productivity rollup. */
export interface IWorkPipelineProductivityRow {
  actor: string;
  actorName: string;
  /** YYYY-MM-DD (UTC). */
  day: string;
  claimed: number;
  hunts: number;
  sessions: number;
  activeMinutes: number;
  averageSessionMinutes: number;
  longestSessionMinutes: number;
  averagePerHour: number;
  firstActivityAt: string | null;
  lastActivityAt: string | null;
  buckets: Record<string, number>;
  bucketMinutes: Record<string, number>;
  activities: Record<string, number>;
  completed: number;
  released: number;
  escalated: number;
  reopened: number;
  entityEdits: number;
  domainsTouched: number;
  domains: string[];
}

export interface IWorkPipelineProductivityFilter {
  actor?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ── Recompute (fire-and-forget, polled) ────────────────────────

export type WorkPipelineRecomputeState =
  | "idle"
  | "running"
  | "complete"
  | "error";

export interface IWorkPipelineRecomputeStatus {
  state: WorkPipelineRecomputeState;
  scope: "list" | "sweep" | null;
  listId: string | null;
  listName: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  itemsDone: number;
  itemsTotal: number;
  listsDone: number;
  listsTotal: number;
  error: string | null;
}

/**
 * Extra signals the derivation needs that don't live on the snapshot metrics
 * themselves (they live on the template record / the freshness clock).
 */
export interface IWorkPipelineDerivationContext {
  snapshotState: WorkPipelineSnapshotState;
  /**
   * True when a working template was synced to SQL AFTER the last SDK test.
   * This is what sends a brand back to APK testing for re-validation rather
   * than leaving it in Dev fixes — the dev built/fixed, a tester must retest.
   */
  templateResyncedSinceLastTest?: boolean;
  /**
   * True when the agentic/session runner recorded a template-specific failure
   * for this domain. Routes to TemplateReview so the template team can inspect
   * and fix before re-entering APK testing.
   */
  lastSessionRunFailed?: boolean;
  /**
   * True when the site cannot be reached by an unattended bot — a login wall or
   * a bot/CAPTCHA wall — and no usable authenticated session has been captured.
   *
   * A tester signs in with Kindred's test account for the merchant and records a
   * session through the checkout extension; replay then uses it. This is not a
   * template fault and no amount of retrying fixes it, which is why it gets a
   * stage of its own rather than sitting in Dev fixes forever.
   */
  needsAuthenticatedSession?: boolean;
  /** Open coupon issues for this domain + geo, per type. */
  openIssues?: Partial<TCouponIssueCounts>;
  /** Freshness window in days; defaults to the shared SDK_TEST_FRESH_DAYS. */
  freshDays?: number;
  /** Start of today's cycle when the brand is on a daily list; null otherwise. */
  dailySince?: Date | string | null;
  /** When a tester last finished a code stage (hunt, test or review) on the brand. */
  codesCheckedAt?: Date | string | null;
  /** Why the agent needs help, on a row the agent put on the board for it; null otherwise. */
  agentHelpReason?: string | null;
}

export interface IWorkPipelineDerivationResult {
  stage: WorkPipelineStage;
  reason: string;
}

const pct = (v: number) => `${Math.round((v ?? 0) * 100)}%`;

/** "2 to suspend · 1 to re-enable", or null when no code needs anything. */
export function describeCodeIssues(
  issues: Partial<TCouponIssueCounts>
): string | null {
  const suspend = issues[CouponIssueType.NeedsSuspension] ?? 0;
  const failing = issues[CouponIssueType.FailingForShoppers] ?? 0;
  const activate = issues[CouponIssueType.NeedsActivation] ?? 0;
  const parts = [
    suspend ? `${suspend} rejected in our test` : null,
    failing ? `${failing} failing for shoppers` : null,
    activate ? `${activate} to re-enable` : null,
  ].filter(Boolean);
  return parts.length ? `Codes: ${parts.join(" · ")}.` : null;
}

/**
 * Pure stage derivation. Given the snapshot metrics and a little context,
 * return the single stage a brand belongs in plus a human-readable reason.
 * Kept pure + shared so the backend recompute and the UI never disagree.
 */
export function deriveWorkPipelineStage(
  metrics: IFullAutoCouponsTargetingMetrics | null,
  ctx: IWorkPipelineDerivationContext
): IWorkPipelineDerivationResult {
  const freshDays = ctx.freshDays ?? SDK_TEST_FRESH_DAYS;

  // Raised issues name a code or a site directly, so they need no snapshot.
  const issues = ctx.openIssues ?? {};
  const codeIssueReason = describeCodeIssues(issues);
  if (codeIssueReason) {
    return { stage: WorkPipelineStage.CouponReview, reason: codeIssueReason };
  }
  if ((issues[CouponIssueType.CouponBoxNotFound] ?? 0) > 0) {
    return {
      stage: WorkPipelineStage.TemplateReview,
      reason:
        'Shoppers got "Coupon box not found" — the template could not find where to enter the code.',
    };
  }
  // Like an issue, the agent's request names the site directly, so it needs no snapshot.
  if (ctx.agentHelpReason) {
    return { stage: WorkPipelineStage.TemplateBuilding, reason: ctx.agentHelpReason };
  }

  // 0. Snapshot problems short-circuit everything else.
  if (ctx.snapshotState === "error" || metrics?.overallStatus === "error") {
    return {
      stage: WorkPipelineStage.Error,
      reason: "Snapshot run hit an error — needs a re-run.",
    };
  }
  if (!metrics || ctx.snapshotState === "missing") {
    return {
      stage: WorkPipelineStage.AwaitingSnapshot,
      reason: "No snapshot yet — add to the snapshot run to begin.",
    };
  }
  if (ctx.snapshotState === "stale") {
    return {
      stage: WorkPipelineStage.AwaitingSnapshot,
      reason: "Snapshot is stale — re-run to refresh the data.",
    };
  }

  // 1. No active coupon => tester hunts for codes.
  if ((metrics.activeCouponCount ?? 0) === 0) {
    return {
      stage: WorkPipelineStage.CodeHunting,
      reason: "No active coupon — hunt for a code to add to the DB.",
    };
  }

  // 2. Code testing — every active coupon needs metadata.
  if ((metrics.metadataCoveragePct ?? 0) < 1) {
    return {
      stage: WorkPipelineStage.CodeTesting,
      reason: `Metadata coverage ${pct(metrics.metadataCoveragePct)} (${
        metrics.couponsWithMetadataCount ?? 0
      }/${metrics.activeCouponCount}).`,
    };
  }

  const dailySince = ctx.dailySince ? new Date(ctx.dailySince) : null;
  const codesCheckedAt = ctx.codesCheckedAt ? new Date(ctx.codesCheckedAt) : null;
  if (dailySince && (!codesCheckedAt || codesCheckedAt < dailySince)) {
    return {
      stage: WorkPipelineStage.CodeTesting,
      reason: "Daily list — check today's codes are live and up to date.",
    };
  }

  // 3. Targeting. Coverage/metadata disagreement lands here rather than in
  // code testing — it's about how a coupon is targeted, not whether it works.
  const coverageMismatch = metrics.couponMetadataCoverageMismatchCount ?? 0;
  if ((metrics.targetingCoveragePct ?? 0) < 1 || coverageMismatch > 0) {
    return {
      stage: WorkPipelineStage.Targeting,
      reason:
        coverageMismatch > 0
          ? `Coverage disagrees with metadata on ${coverageMismatch} coupon(s).`
          : `Targeting coverage ${pct(
              metrics.targetingCoveragePct
            )} (${metrics.couponsWithTargetingCount ?? 0} coupon(s) targeted).`,
    };
  }

  // 2.5. A wall the runner cannot get through. Placed BEFORE template building
  // on purpose: there is no point asking a developer to build a template for a
  // checkout the runner can never load. Codes and targeting sit above this
  // because they are database work that never touches the site.
  if (ctx.needsAuthenticatedSession) {
    return {
      stage: WorkPipelineStage.SessionCapture,
      reason:
        "Site needs a signed-in session — capture one with the test account via the checkout extension.",
    };
  }

  // 4. Template building (developer).
  if (!metrics.hasAutoTemplate || !metrics.hasSkuTemplate) {
    const missing = [
      !metrics.hasAutoTemplate ? "auto" : null,
      !metrics.hasSkuTemplate ? "sku" : null,
    ]
      .filter(Boolean)
      .join(" + ");
    return {
      stage: WorkPipelineStage.TemplateBuilding,
      reason: `Missing ${missing} template.`,
    };
  }

  // 4.5. Template review — session run flagged a template failure.
  if (ctx.lastSessionRunFailed) {
    return {
      stage: WorkPipelineStage.TemplateReview,
      reason:
        "Automated session run flagged a template failure — review and fix before retesting.",
    };
  }

  // 5. SDK / APK testing gate. DB-level checks are all green here.
  const lastResult = metrics.lastSdkTestResult;
  const lastAt = metrics.lastSdkTestAt;

  // A failed test that has NOT been re-synced by a dev => Dev fixes.
  if (lastResult === "fail" && !ctx.templateResyncedSinceLastTest) {
    return {
      stage: WorkPipelineStage.DevFixes,
      reason: "APK test failed — needs a developer fix.",
    };
  }

  // A daily brand's APK test has to follow today's code check.
  const testedAfterCodes =
    !dailySince ||
    (lastResult === "pass" &&
      !!lastAt &&
      !!codesCheckedAt &&
      new Date(lastAt) >= codesCheckedAt);

  // Passing + fresh => Complete.
  if (metrics.sdkTestStatus === "green" && testedAfterCodes) {
    return {
      stage: WorkPipelineStage.Complete,
      reason: "All checks green and SDK test fresh.",
    };
  }

  // Everything else (missing / stale / just re-synced fix) => APK testing.
  let reason = "Ready for APK testing.";
  if (!lastResult || !lastAt) {
    reason = "No SDK test yet — ready for APK testing.";
  } else if (ctx.templateResyncedSinceLastTest) {
    reason = "Template re-synced — retest on APK.";
  } else if (!testedAfterCodes) {
    reason = "Daily list — codes checked, APK test today.";
  } else if (lastResult === "pass") {
    const ageDays = Math.floor(
      (Date.now() - new Date(lastAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    reason = `SDK test ${ageDays} day(s) old (> ${freshDays}) — retest.`;
  }
  return { stage: WorkPipelineStage.ApkTesting, reason };
}
